import React, { useState } from 'react';

import { Box, CircularProgress, Typography, IconButton, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { ArrowBack } from '@material-ui/icons';
import { Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, ComposedChart, Cell } from 'recharts';

const useStyles = makeStyles(() => ({
  backButton: {
    marginRight: 8,
  },
  chartContainer: {
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    padding: '20px',
  },
  chartTitle: {
    alignItems: 'center',
    borderBottom: '1px solid #eaeaea',
    display: 'flex',
    fontSize: 18,
    fontWeight: 600,
    marginBottom: '20px',
    paddingBottom: '10px',
  },
  emptyState: {
    alignItems: 'center',
    color: '#999',
    display: 'flex',
    fontSize: 14,
    height: 400,
    justifyContent: 'center',
  },
  errorState: {
    alignItems: 'center',
    color: '#d32f2f',
    display: 'flex',
    flexDirection: 'column',
    fontSize: 14,
    gap: 10,
    height: 400,
    justifyContent: 'center',
  },
  legendItem: {
    alignItems: 'center',
    display: 'flex',
    fontSize: 12,
    gap: 8,
    marginBottom: 8,
  },
  legendSquare: {
    borderRadius: 2,
    height: 12,
    width: 12,
  },
  loadingContainer: {
    alignItems: 'center',
    display: 'flex',
    height: 400,
    justifyContent: 'center',
  },
  statItem: {
    textAlign: 'center',
  },
  statLabel: {
    color: '#666',
    fontSize: 12,
    marginBottom: 4,
  },
  statValue: {
    color: '#172B4D',
    fontSize: 20,
    fontWeight: 600,
  },
  statsContainer: {
    borderTop: '1px solid #eaeaea',
    display: 'flex',
    gap: 30,
    justifyContent: 'center',
    marginTop: 20,
    paddingTop: 15,
  },
}));

const COLORS = [
  '#0088FE',
  '#00C49F',
  '#FFBB28',
  '#FF8042',
  '#8884D8',
  '#FF6B9D',
  '#C23531',
  '#2F4554',
  '#61A0A8',
  '#D48265',
  '#91C7AE',
  '#749F83',
  '#CA8622',
  '#BDA29A',
  '#6E7074',
];

const CategoryParetoChart = ({ data, endDate, error, loading, startDate }) => {
  const classes = useStyles();
  const [selectedCategory, setSelectedCategory] = useState(null);

  if (loading) {
    return (
      <Box className={classes.chartContainer}>
        <Box className={classes.chartTitle}>
          <Typography>Category Pareto Analysis</Typography>
        </Box>
        <Box className={classes.loadingContainer}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={classes.chartContainer}>
        <Box className={classes.chartTitle}>
          <Typography>Category Pareto Analysis</Typography>
        </Box>
        <Box className={classes.errorState}>
          <Typography>Failed to load category data</Typography>
          <Typography variant="caption">{error}</Typography>
        </Box>
      </Box>
    );
  }

  if (!data || !data.data || data.data.length === 0) {
    return (
      <Box className={classes.chartContainer}>
        <Box className={classes.chartTitle}>
          <Typography>Category Pareto Analysis</Typography>
        </Box>
        <Box className={classes.emptyState}>
          <Typography>No categorized observations found for this period</Typography>
        </Box>
      </Box>
    );
  }

  const handleCategoryClick = (categoryValue) => {
    const category = data.data.find((cat) => cat.categoryValue === categoryValue);
    if (category && category.subcategories && category.subcategories.length > 0) {
      setSelectedCategory(category);
    }
  };

  const handleBackToCategories = () => {
    setSelectedCategory(null);
  };

  const handleBarClick = (data, index) => {
    if (!selectedCategory && data && data.categoryValue) {
      handleCategoryClick(data.categoryValue);
    }
  };

  const sourceData = selectedCategory
    ? selectedCategory.subcategories.map((item) => ({
        category: item.label,
        categoryValue: item.subcategoryValue,
        count: item.count,
        percentage: item.percentage,
      }))
    : data.data;

  const sortedData = [...sourceData].sort((a, b) => b.count - a.count);

  const totalCount = sortedData.reduce((sum, item) => sum + item.count, 0);

  let cumulativeCount = 0;
  const paretoData = sortedData.map((item) => {
    cumulativeCount += item.count;
    const cumulativePercentage = (cumulativeCount / totalCount) * 100;

    return {
      category: item.category,
      categoryValue: item.categoryValue,
      count: item.count,
      cumulativePercentage: Math.round(cumulativePercentage * 100) / 100,
      percentage: item.percentage,
    };
  });

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box
          style={{
            backgroundColor: 'white',
            border: '1px solid #ccc',
            borderRadius: '4px',
            padding: '10px',
          }}>
          <Typography style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{data.category}</Typography>
          <Typography style={{ fontSize: 11 }}>Count: {data.count}</Typography>
          <Typography style={{ color: '#666', fontSize: 11 }}>Individual: {data.percentage}%</Typography>
          <Typography style={{ color: '#FF8042', fontSize: 11 }}>Cumulative: {data.cumulativePercentage}%</Typography>
        </Box>
      );
    }
    return null;
  };

  const renderLegendBelow = () => {
    return (
      <Box display="flex" flexDirection="column" flexWrap="wrap" justifyContent="center" marginTop="20px" paddingTop="15px" borderTop="1px solid #eaeaea">
        {paretoData.map((item, index) => {
          const displayName = item.category;
          const displayCount = item.count;
          const itemKey = item.categoryValue;

          const originalCategory = !selectedCategory && item.categoryValue ? data.data.find((cat) => cat.categoryValue === item.categoryValue) : null;
          const hasSubcategories = originalCategory && originalCategory.subcategories && originalCategory.subcategories.length > 0;

          return (
            <Tooltip
              key={itemKey}
              title={
                <Typography variant="body2">
                  {displayName}: {displayCount} ({item.percentage}%)
                </Typography>
              }>
              <Box
                className={classes.legendItem}
                onClick={() => !selectedCategory && hasSubcategories && handleCategoryClick(item.categoryValue)}
                style={{ cursor: !selectedCategory && hasSubcategories ? 'pointer' : 'default' }}>
                <Box className={classes.legendSquare} style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                <Typography>
                  {displayName} ({displayCount})
                </Typography>
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    );
  };

  const eightyPercentLine = paretoData.findIndex((item) => item.cumulativePercentage >= 80);
  const vitalFew = eightyPercentLine >= 0 ? eightyPercentLine + 1 : paretoData.length;

  return (
    <Box className={classes.chartContainer}>
      <Box className={classes.chartTitle}>
        {selectedCategory && (
          <IconButton size="small" onClick={handleBackToCategories} className={classes.backButton}>
            <ArrowBack />
          </IconButton>
        )}
        <Typography>
          {selectedCategory ? `${selectedCategory.category} - Subcategories` : 'Category Pareto Analysis'}
          {startDate && endDate && (
            <Typography component="span" variant="caption" style={{ color: '#666', fontWeight: 400, marginLeft: 8 }}>
              ({startDate} - {endDate})
            </Typography>
          )}
        </Typography>
      </Box>

      <ResponsiveContainer width="100%" height={400}>
        <ComposedChart
          data={paretoData}
          margin={{
            top: 20,
          }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis tick={false} />
          <YAxis yAxisId="left" label={{ angle: -90, position: 'insideLeft', style: { fontSize: 14 }, value: '# of Errors' }} />
          <YAxis yAxisId="right" orientation="right" domain={[0, 100]} label={{ angle: 90, position: 'insideRight', style: { fontSize: 14 }, value: 'Cumulative %' }} />
          <RechartsTooltip content={<CustomTooltip />} animationDuration={0} isAnimationActive={false} />
          <Bar yAxisId="left" dataKey="count" barSize={60} onClick={handleBarClick} style={{ cursor: selectedCategory ? 'default' : 'pointer' }}>
            {paretoData.map((entry, index) => (
              <Cell key={`cell-${Math.random()}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Bar>
          <Line yAxisId="right" type="monotone" dataKey="cumulativePercentage" stroke="#555" strokeWidth={2} dot={{ fill: '#555', r: 5 }} />
        </ComposedChart>
      </ResponsiveContainer>

      {renderLegendBelow()}

      <Box className={classes.statsContainer}>
        <Box className={classes.statItem}>
          <Typography className={classes.statLabel}>Total Categories</Typography>
          <Typography className={classes.statValue}>{paretoData.length}</Typography>
        </Box>
        <Box className={classes.statItem}>
          <Typography className={classes.statLabel}>Vital Few (80%)</Typography>
          <Typography className={classes.statValue}>{vitalFew}</Typography>
        </Box>
        <Box className={classes.statItem}>
          <Typography className={classes.statLabel}>Total Observations</Typography>
          <Typography className={classes.statValue}>{totalCount}</Typography>
        </Box>
        <Box className={classes.statItem}>
          <Typography className={classes.statLabel}>Top Category</Typography>
          <Typography className={classes.statValue}>{paretoData[0]?.percentage}%</Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default CategoryParetoChart;
